import { createSlice } from '@reduxjs/toolkit';

const getSessionValue = (key, defaultValue) => {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
};

const initialState = {
    filters: [],
    selectedPortfolioCompany: getSessionValue('selectedPortfolioCompany', []),
    portfolioItems: [],
    divisionOptions: [],
    selectedDivisions: getSessionValue('selectedDivisions', []),
    stageOptions: [],
    selectedStages: getSessionValue('selectedStages', []),
    opsLeaderOptions: [],
    selectedOpsLeaders: getSessionValue('selectedOpsLeaders', []),
    projectTypeOptions: [],
    selectedProjectTypes: getSessionValue('selectedProjectTypes', []),
    accountOptions: [],
    selectedAccounts: getSessionValue('selectedAccounts', []),
    monthOptions: [],
    selectedMonth: getSessionValue('selectedMonth', 'Latest'),
    portfolioCompanies: [],
    sortField: getSessionValue('sortField', 'project_name'),
    pageNumber: 1,
    totalResults: 0,
    hasMoreResults: false,
    portfolioError: null,
    prevPageExists: false,
    nextPageExists: false,
    resultsPerPage: 25,
    portfolioTeamData: [],
    portfolioProjects: [],
    portfolioSearchTerm: '',
};

const setSessionAndState = (state, key, value) => {
    state[key] = value;
    sessionStorage.setItem(key, JSON.stringify(value));
};

const portfolioSlice = createSlice({
    name: 'portfolio',
    initialState,
    reducers: {
        setFilters: (state, action) => { state.filters = action.payload; },
        setSelectedPortfolioCompany: (state, action) => { setSessionAndState(state, 'selectedPortfolioCompany', action.payload); },
        setPortfolioItems: (state, action) => { state.portfolioItems = action.payload; },
        addPortfolioItem: (state, action) => { state.portfolioItems.push(action.payload); },
        removePortfolioItem: (state, action) => { state.portfolioItems = state.portfolioItems.filter(item => item.id !== action.payload); },
        setDivisionOptions: (state, action) => { state.divisionOptions = action.payload; },
        setSelectedDivisions: (state, action) => { setSessionAndState(state, 'selectedDivisions', action.payload); },
        setStageOptions: (state, action) => { state.stageOptions = action.payload; },
        setSelectedStages: (state, action) => { setSessionAndState(state, 'selectedStages', action.payload); },
        setOpsLeaderOptions: (state, action) => { state.opsLeaderOptions = action.payload; },
        setSelectedOpsLeaders: (state, action) => { setSessionAndState(state, 'selectedOpsLeaders', action.payload); },
        setProjectTypeOptions: (state, action) => { state.projectTypeOptions = action.payload; },
        setSelectedProjectTypes: (state, action) => { setSessionAndState(state, 'selectedProjectTypes', action.payload); },
        setAccountOptions: (state, action) => { state.accountOptions = action.payload; },
        setSelectedAccounts: (state, action) => { setSessionAndState(state, 'selectedAccounts', action.payload); },
        setMonthOptions: (state, action) => { state.monthOptions = action.payload; },
        setSelectedMonth: (state, action) => { setSessionAndState(state, 'selectedMonth', action.payload); },
        setPortfolioCompanies: (state, action) => { state.portfolioCompanies = action.payload; },
        setSortField: (state, action) => { setSessionAndState(state, 'sortField', action.payload); },
        setPageNumber: (state, action) => { state.pageNumber = action.payload; },
        setTotalResults: (state, action) => { state.totalResults = action.payload; },
        setHasMoreResults: (state, action) => { state.hasMoreResults = action.payload; },
        setPortfolioError: (state, action) => { state.portfolioError = action.payload; },
        setPrevPageExists: (state, action) => { state.prevPageExists = action.payload; },
        setNextPageExists: (state, action) => { state.nextPageExists = action.payload; },
        setPortfolioTeamData: (state, action) => { state.portfolioTeamData = action.payload; },
        setPortfolioProjects: (state, action) => { state.portfolioProjects = action.payload; },
        setPortfolioSearchTerm: (state, action) => { state.portfolioSearchTerm = action.payload; },
        setAllFilters: (state, action) => {
            const {
                selectedPortfolioCompany,
                selectedDivisions,
                selectedStages,
                selectedOpsLeaders,
                selectedProjectTypes,
                selectedAccounts,
                selectedMonth,
                sortField
            } = action.payload;

            setSessionAndState(state, 'selectedPortfolioCompany', selectedPortfolioCompany);
            setSessionAndState(state, 'selectedDivisions', selectedDivisions);
            setSessionAndState(state, 'selectedStages', selectedStages);
            setSessionAndState(state, 'selectedOpsLeaders', selectedOpsLeaders);
            setSessionAndState(state, 'selectedProjectTypes', selectedProjectTypes);
            setSessionAndState(state, 'selectedAccounts', selectedAccounts);
            setSessionAndState(state, 'selectedMonth', selectedMonth);
            setSessionAndState(state, 'sortField', sortField);
        },
        setAllFilterOptions: (state, action) => {
            const {
                divisionOptions,
                stageOptions,
                opsLeaderOptions,
                projectTypeOptions,
                accountOptions,
                monthOptions
            } = action.payload;

            state.divisionOptions = divisionOptions;
            state.stageOptions = stageOptions;
            state.opsLeaderOptions = opsLeaderOptions;
            state.projectTypeOptions = projectTypeOptions;
            state.accountOptions = accountOptions;
            state.monthOptions = monthOptions;
        }
    },
});

export const {
    setFilters,
    setSelectedPortfolioCompany,
    setPortfolioItems,
    addPortfolioItem,
    removePortfolioItem,
    setDivisionOptions,
    setSelectedDivisions,
    setStageOptions,
    setSelectedStages,
    setOpsLeaderOptions,
    setSelectedOpsLeaders,
    setProjectTypeOptions,
    setSelectedProjectTypes,
    setAccountOptions,
    setSelectedAccounts,
    setPortfolioCompanies,
    setSortField,
    setPageNumber,
    setTotalResults,
    setHasMoreResults,
    setPortfolioError,
    setPrevPageExists,
    setNextPageExists,  
    setPortfolioTeamData,
    setPortfolioProjects,
    setMonthOptions,
    setSelectedMonth,
    setAllFilters,
    setAllFilterOptions,
    setPortfolioSearchTerm,
} = portfolioSlice.actions;

export default portfolioSlice.reducer;